import React, {useRef, useState, useMemo} from 'react';
import {produce} from 'immer';
import {WordCloudData} from 'entities/event';
import {ChartQuery} from 'entities/chart';
import {WordCloud} from '@/views/storyboard/charts/wordcloud/WordCloud';
import useGlobalFiltersCombined from '@/views/storyboard/filters/useGlobalFiltersCombined';
import mergeLocalAndGlobalFilters from '@/views/storyboard/filters/mergeLocalAndGlobalFilters';
import {
    useTooltip,
    useTooltipInPortal,
    TooltipWithBounds,
} from '@visx/tooltip';
import i18n from 'i18n';

interface Props {
    data: WordCloudData[];
    chartQuery: ChartQuery;
    queryDuration?: string;
    chartSettingsView?: boolean;
}

export const WordCloudWrapper = React.memo<Props>(({
    data,
    chartQuery,
    queryDuration = '0',
    chartSettingsView = false,
}) => {
    const [prevData, setPrevData] = useState<WordCloudData[]>([]);
    const [globalAllFields, globalAdditionalFilters] = useGlobalFiltersCombined();
    const [finalAllFields, finalAdditionalFilters] = mergeLocalAndGlobalFilters(
        chartQuery.all_fields,
        chartQuery.additionalFilters,
        globalAllFields,
        globalAdditionalFilters,
    );

    const finalChartQuery = useMemo(() => {
        return produce(chartQuery, (draft) => {
            draft.all_fields = finalAllFields;
            draft.additionalFilters = finalAdditionalFilters;
        });
    }, [chartQuery, finalAllFields, finalAdditionalFilters]);

    if (prevData !== data) {
        setPrevData(data);
    }

    const wcRef = useRef<SVGSVGElement>(null);

    const {
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
        showTooltip,
        hideTooltip,
    } = useTooltip<WordCloudData>();

    const {containerRef, containerBounds, TooltipInPortal} = useTooltipInPortal({
        scroll: true,
    });
    const handleWordMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target instanceof SVGTSpanElement) {
            const fullItem = data.find((x) => x[0] === (event.target as SVGTSpanElement).textContent);
            const containerX = event.target.getBoundingClientRect().x - event.currentTarget.getBoundingClientRect().x;
            const containerY = event.target.getBoundingClientRect().y - event.currentTarget.getBoundingClientRect().y + 20;
            showTooltip({
                tooltipLeft: containerX,
                tooltipTop: containerY,
                tooltipData: fullItem,
            });
        }
    };

    const handleOnMouseLeave = () => {
        hideTooltip();
    };

    return (
        <div
            className="w-100 h-100 pt-2 px-3 pb-3 text-center position-relative"
            style={{cursor: 'pointer'}}
            ref={containerRef}
        >

            <div style={{width: '100%', height: '100%', overflow: 'hidden'}}>
                {useMemo(() => {
                    return <WordCloud
                        // TODO: Probably should reverse forwardRef & memo positions
                        // @ts-ignore
                        ref={wcRef}
                        data={data}
                        finalChartQuery={finalChartQuery}
                        onWordMouseOver={handleWordMouseOver}
                        onWordMouseLeave={handleOnMouseLeave}
                    />
                }, [data])}
            </div>

            {tooltipOpen && tooltipData && (
                <TooltipWithBounds
                    key={Math.random()} // Ensure the tooltip updates with each new word
                    top={tooltipTop}
                    left={tooltipLeft}
                >
                    <div style={{textAlign: 'left'}}>
                        <p style={{margin: '5px'}}>{i18n.t('word_txt.text')} {tooltipData[0]}</p>
                        <p style={{margin: '5px'}}>{i18n.t('count_txt.text')} {tooltipData[2]}</p>
                    </div>
                </TooltipWithBounds>
            )}
            {!chartSettingsView ? (
                <div className="chart-query-time-indicator-wc">
                    <p>
                        {i18n.t('table_chart_query_duration.text', {queryDuration})}
                    </p>
                </div>
            ) : null}
        </div>
    );
});
